import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { CalendarRange } from "lucide-react"; // VIEW_TOGGLE_PARKED: ListTree, Orbit
import { Segmented, DatePicker, Divider } from "antd";
import dayjs from "dayjs";
import { DASHBOARD_BASE } from "../../../../config/featureFlags";
import { useDashTheme } from "../../hooks/useDashTheme";
// Two-tier drill-down, shared with the Classic dashboard so clicking a person
// or a project behaves identically in both views — and reads both trackers.
import { DrilldownDrawer } from "../../components/drilldown";
import { ModulePanel } from "./ModulePanel";
// VoiceAssistant is mounted behind a commented-out block below; the import
// stays out of the bundle until that block is restored.
// RAIL_PARKED: import { RightRail } from "./RightRail";
// 2026-08-30 — Graphical ↔ Brain switch + the Graphical dashboard's own
// insight panels rendered inside the Brain (Insights tab on the rail).
import { DashViewSwitch } from "../../components/DashViewSwitch";
// RAIL_PARKED: import { BrainInsights } from "./BrainInsights";
// RAIL_PARKED: import { Sparkles, Brain as BrainIcon } from "lucide-react";
import { useBrainData } from "./useBrainData";
// 2026-08-30 (user): a click on the orbit opens the FULL graphical drill page
// (AI read, chapters, what they worked on, who did how much) instead of the
// side drawer. Paths are built against GRAPHICAL_BASE explicitly — drillBase()
// would read /brain from the URL and hand back the classic drill.
import { GRAPHICAL_BASE, filterQuery } from "../../components/drilldown/drilldownRoutes";
import { OrbitSkeleton, ListSkeleton } from "./OrbitSkeleton";
import { DashAntd } from "../../components/DashAntd";
import { fmtH } from "./brainSignals.mjs";

/* Heavy chunks load on demand so the dashboard shell paints immediately:
   - BrainCanvas drags in the whole ReactFlow graph library
   - ListView only matters when the user flips the toggle */
const BrainCanvas = React.lazy(() => import("./BrainCanvas").then((m) => ({ default: m.BrainCanvas })));
const ListView = React.lazy(() => import("./ListView"));
/* PersonPanel / PersonStage are no longer mounted: person clicks now open the
   shared DrilldownDrawer, the same component the Classic dashboard uses. Both
   files stay on disk (PersonBrain, the AI chat, is still used — the drawer
   mounts it as its Level-2 "Brain" tab). */

/* Company Brain — the dashboard as a living control centre. Centre: the org as
   a radial graph around a pulsing brain (health score). Right: what the brain
   thinks you should do, and what is happening right now. Bottom: one command
   bar, voice or text, that either acts locally (navigate / focus / theme) or
   hands the question to Ask TG. */

const KEYFRAMES = `
@keyframes tgBrainPulse { 0% { transform: scale(1); opacity: .7; } 70% { transform: scale(1.18); opacity: 0; } 100% { opacity: 0; } }
.tg-brain-halo { animation: tgBrainPulse 3.2s ease-out infinite; }
.tg-brain-halo-2 { animation-delay: 1.6s; }
@keyframes tgLiveDot { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
.tg-live-dot { animation: tgLiveDot 1.6s ease-in-out infinite; }
@keyframes tgMicPulse { 0% { box-shadow: 0 0 0 0 rgba(229,72,77,.45); } 100% { box-shadow: 0 0 0 12px rgba(229,72,77,0); } }
.tg-mic-live { animation: tgMicPulse 1.2s ease-out infinite; }
@keyframes tgSpin { to { transform: rotate(360deg); } }
@keyframes tgBreath { 0%,100% { transform: scale(1); opacity: .8; } 50% { transform: scale(1.12); opacity: 1; } }
.tg-brain-breath { animation: tgBreath 2.8s ease-in-out infinite; }
`;

export default function BrainV2() {
  const t = useDashTheme();
  const d = useBrainData();
  const navigate = useNavigate();

  const [view, setView] = React.useState("orbit"); // orbit | list — toggle parked (VIEW_TOGGLE_PARKED)
  void setView;
  // Right rail: the Brain's own signals, or the Graphical dashboard's insight
  // panels for the same window. Remembered per browser.
  const [railTab, setRailTab] = React.useState(() => { try { return localStorage.getItem("tg.brainRail") === "insights" ? "insights" : "brain"; } catch (_) { return "brain"; } });
  const pickRail = React.useCallback((v) => { setRailTab(v); try { localStorage.setItem("tg.brainRail", v); } catch (_) {} }, []);
  void railTab; void pickRail; // RAIL_PARKED — used only by the parked right rail
  const [drawer, setDrawer] = React.useState(null); // { kind:'project', id, name }
  const [panelPerson, setPanelPerson] = React.useState(null); // person drill-down subject
  const [modulePanel, setModulePanel] = React.useState(null); // module explainer slide-in
  // focusRequest survives for canvas focusing; nothing sets it since the
  // command bar left, but the canvas API still accepts it.
  const [focusRequest] = React.useState(null);

  // Resolve any member reference (canvas node, move subject, update row) to a
  // full person object so the panel always has metrics to show.
  const personFor = React.useCallback((id, name) => {
    const p = d.people.find((x) => String(x.id) === String(id));
    if (p) return p;
    const m = d.members.find((x) => String(x.userId) === String(id));
    return {
      id: String(id), name: name || m?.name || "Member", position: m?.position || "",
      totalMinutes: m?.totalMinutes || 0, idleRatio: 0, streak: m?.streak || 0,
      lastActivity: m?.lastActivity || null, below: 0, expanded: false,
      status: m?.hasData ? "steady" : "silent",
    };
  }, [d.people, d.members]);

  const drillQuery = React.useCallback((name) => {
    const q = new URLSearchParams(filterQuery(d.range, null));
    if (name) q.set("name", String(name));
    q.set("from", "brain"); // the drill's back button returns here
    return q.toString();
  }, [d.range]);

  const openMember = React.useCallback((p) => {
    const person = personFor(p.id || p.userId, p.name);
    setModulePanel(null);
    navigate(`${GRAPHICAL_BASE}/person/${encodeURIComponent(person.id)}?${drillQuery(person.name)}`);
  }, [personFor, navigate, drillQuery]);

  // The panel must see the LIVE person (expanded flag, fresh metrics), not the
  // snapshot taken at click time — otherwise its collapse button double-toggles.
  const livePanelPerson = React.useMemo(() => {
    if (!panelPerson) return null;
    return d.people.find((x) => String(x.id) === String(panelPerson.id)) || panelPerson;
  }, [panelPerson, d.people]);
  const openProject = React.useCallback((p) => {
    setModulePanel(null);
    const id = String(p._id || p.id || p.projectId);
    navigate(`${GRAPHICAL_BASE}/project/${encodeURIComponent(id)}?${drillQuery(p.name || p.projectName)}`);
  }, [navigate, drillQuery]);

  /* One subject drives the drill-down, whichever way it was opened. Person
     clicks still go through `panelPerson` because the canvas keys off it
     (it unfolds the manager's reports and hides the orbit controls). */
  const drillSubject = React.useMemo(() => {
    if (livePanelPerson) {
      return {
        kind: "person",
        id: String(livePanelPerson.id),
        name: livePanelPerson.name,
        position: livePanelPerson.position || "",
      };
    }
    return drawer;
  }, [livePanelPerson, drawer]);

  const closeDrill = React.useCallback(() => { setPanelPerson(null); setDrawer(null); }, []);

  const runMoveAction = React.useCallback((mv) => {
    if (mv.subject.type === "member" && mv.action.type === "member") {
      setPanelPerson(personFor(mv.subject.id, mv.subject.name));
    } else if (mv.subject.type === "project") {
      setDrawer({ kind: "project", id: String(mv.subject.id), name: mv.subject.name });
    } else if (mv.action.type === "team") {
      navigate(`${DASHBOARD_BASE}/team`);
    } else if (mv.action.type === "summary") {
      navigate(`${DASHBOARD_BASE}/work-summary`);
    }
  }, [navigate, personFor]);

  // ── Voice assistant replaces the old command bar: one orb, any language,
  //    answers scoped server-side to the people the viewer can see. ──

  // Module lobes carry live counts — each one tells you whether it's worth
  // opening, instead of being a bare navigation button.
  const modules = React.useMemo(() => {
    const anomalies = d.insightEntries.filter((e) => e.type === "anomaly").length;
    const ideas = d.insightEntries.filter((e) => e.type === "productive").length;
    /* 2026-08-30 (user): every lobe opens a page that actually WRITES something
       from live data. The old subs ("no new" / "clear") came from the dead
       DailySummary feed, so they always read empty. */
    void ideas; void anomalies;
    const quiet = d.members.filter((m) => !m.hasData).length;
    return [
      { key: "org-intelligence", code: "INS", label: "Org Intelligence", sub: "blockers · signals · AI brief", subTone: t.c.ai },
      { key: "work-summary", code: "SUM", label: "Work Summary", sub: `${fmtH(d.kpi?.totalMinutes || 0)} · AI day brief` },
      { key: "app-usage", code: "APP", label: "Where hours went", sub: "tools · time · drill" },
      { key: "patterns", code: "PAT", label: "Today's story", sub: quiet > 0 ? `${quiet} silent · written live` : "written live", subTone: quiet > 0 ? t.status.warning : undefined },
      { key: "ask", code: "ASK", label: "Ask TG", sub: "voice · text" },
    ];
  }, [d.insightEntries, d.kpi, t]);

  /* Lobe click → the real AI page for that lobe, carrying the Brain's window.
     The explainer ModulePanel is parked (MODULE_PANEL_PARKED) — it only ever
     showed computed counts, never a written insight. */
  const onModule = React.useCallback((m) => {
    if (m.key === "ask") return; // the command bar below is the Ask surface
    setDrawer(null); setPanelPerson(null); setModulePanel(null);
    const q = new URLSearchParams(filterQuery(d.range, null)); q.set("from", "brain");
    if (m.key === "org-intelligence") navigate(`${DASHBOARD_BASE}/org-intelligence-v2`);
    else if (m.key === "work-summary") navigate(`${DASHBOARD_BASE}/work-summary-new`);
    else if (m.key === "app-usage") navigate(`${GRAPHICAL_BASE}?${q.toString()}#tools`);
    else if (m.key === "patterns") navigate(`${GRAPHICAL_BASE}/today?${q.toString()}`);
  }, [navigate, d.range]);

  const openModuleScreen = React.useCallback((m) => {
    if (m.key === "patterns") setView("list");
    else navigate(`${DASHBOARD_BASE}/${m.key}`);
  }, [navigate]);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  })();

  return (
    <div style={{
      display: "flex", height: "100%", minHeight: 0, background: t.c.bg,
      fontFamily: t.font.family, color: t.c.text, overflow: "hidden",
    }}>
      <style>{KEYFRAMES}</style>

      {/* ═══ Centre stage — canvas above, command bar docked in its own rail
          below so it never floats over people. ═══ */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        {/* Refetching the window rebuilds every number on this canvas. Without
            this the orbit sat there showing the PREVIOUS window's data as if it
            were the new one. Shaped like the orbit itself so it reads as the
            graph re-forming rather than as a grey box. */}
        <AnimatePresence>
          {d.loading && (
            <motion.div
              key="orbit-skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              style={{ position: "absolute", inset: 0, zIndex: 5 }}
            >
              {view === "orbit"
                ? <OrbitSkeleton label={`Reading ${periodLabel(d.period)}…`} />
                : <ListSkeleton />}
            </motion.div>
          )}
        </AnimatePresence>

        {view === "orbit" ? (
          <React.Suspense fallback={
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: t.font.size.sm, color: t.c.textTertiary }}>
              Waking the brain…
            </div>
          }>
            <BrainCanvas
              people={d.people}
              projects={d.projects}
              modules={modules}
              moves={d.moves}
              memberships={d.memberships}
              health={d.health}
              onPerson={openMember}
              onProject={openProject}
              onModule={onModule}
              onMorePeople={() => navigate(`${DASHBOARD_BASE}/team`)}
              focusRequest={focusRequest}
            />
          </React.Suspense>
        ) : (
          <React.Suspense fallback={null}>
            <ListView t={t} d={d} onMove={runMoveAction} onPerson={openMember} />
          </React.Suspense>
        )}

        {/* One header row: greeting/scope left, controls right. A single flex
            row (not two absolutes) so they wrap instead of overlapping when
            the canvas is narrow. */}
        <div id="tg-brain-head" style={{
          position: "absolute", top: 12, left: 20, right: 20, zIndex: 6,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: t.s[3], flexWrap: "wrap", pointerEvents: "none",
        }}>
        {/* 2026-08-30 — one line, not three: greeting · scope · org shape on a
            single baseline, so the overlay stays short and the orbit below it
            gets the room (the canvas fit measures this block). */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", minWidth: 0, pointerEvents: "auto" }}>
          <span style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.medium, whiteSpace: "nowrap" }}>
            {greeting}{d.user?.firstName ? `, ${d.user.firstName}` : ""}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: t.font.size.sm, color: t.c.textSecondary, whiteSpace: "nowrap" }}>
            <span style={{ width: 6, height: 6, borderRadius: 99, background: t.status.positive }} className="tg-live-dot" />
            {d.isAdminScope ? "Whole org" : "Your team"}
            {" · "}{d.directs.length} direct{d.indirectCount > 0 ? ` · ${d.indirectCount} below` : ""}
          </span>
          {/* Org shape at a glance — the data-hygiene line. Admin scope only:
              for a scoped manager "unmanaged" would just describe their slice. */}
          {d.isAdminScope && d.shape.total > 0 && (
            <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
              title={`Reporting depth ${d.shape.depth} · widest level ${d.shape.widest} · average span ${d.shape.avgSpan}${d.shape.unmanaged > 0 ? ` · ${d.shape.unmanaged} people with no manager` : ""}`}>
              depth {d.shape.depth} · widest {d.shape.widest} · span {d.shape.avgSpan}
              {d.shape.unmanaged > 0 && (
                <span style={{ color: t.status.warning }}> · {d.shape.unmanaged} unmanaged</span>
              )}
            </span>
          )}
        </div>

        {/* One toolbar, one baseline. Wrapped so antd renders in the dashboard
            palette rather than the legacy app's — without it the segmented
            track is white on a grey canvas and the row loses all definition. */}
        {/* ONE toolbar, not three floating shapes.
            The controls previously sat loose on the canvas: antd's segmented
            track (#F1F3F7) against the dashboard canvas (#E9ECF1) is a 2%
            step, so each control dissolved into the background and the row had
            no edge, no baseline and no grouping. Wrapping them in a single
            raised surface — the same white card treatment the rest of the
            dashboard uses — is what gives the row definition. */}
        <DashAntd>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap",
          padding: 4, borderRadius: t.r.md, pointerEvents: "auto",
          background: t.c.surface,
          boxShadow: `0 0 0 1px ${t.c.border}, ${t.shadow.sm}`,
        }}>
          <WindowControl t={t} period={d.period} range={d.range} onChange={d.setPeriod} />

          {/* VIEW_TOGGLE_PARKED (2026-08-30, user): Orbit | List switch hidden;
              the canvas stays on Orbit (`view` state + ListView remain wired —
              restore this block to bring the toggle back). */}
          {/*
          <Divider type="vertical" style={{ margin: "0 4px", height: 18, borderColor: t.c.border }} />
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { value: "orbit", label: <Chip icon={<Orbit size={12} />} text="Orbit" /> },
              { value: "list", label: <Chip icon={<ListTree size={12} />} text="List" /> },
            ]}
          />
          */}

          <Divider type="vertical" style={{ margin: "0 4px", height: 18, borderColor: t.c.border }} />

          <DashViewSwitch value="brain" size="sm" />
        </div>
        </DashAntd>
        </div>

      </div>

      {/* ═══ Voice assistant — the futuristic footer. One orb, hold a
          conversation with the whole company in any language. Stands down
          while the person panel is open (it owns the docked bar + ⌘K). */}
      {!panelPerson && (
        <div style={{
          flexShrink: 0, borderTop: `1px solid ${t.c.border}`, background: t.c.surface,
          padding: `${t.s[3]}px ${t.s[5]}px`,
        }}>
          {/* <div style={{ maxWidth: 860, margin: "0 auto" }}>
            <VoiceAssistant t={t} people={d.people} onOpenPerson={openMember} />
          </div> */}
        </div>
      )}
      </div>

      {/* ═══ Right rail — Brain signals | Graphical insights ═══
          RAIL_PARKED (2026-08-30, user): the right section is not needed for
          now. Everything below is intact — uncomment this block (and the
          imports marked RAIL_PARKED at the top) to bring it back. */}
      {/*
      {/* ═══ Right rail — Brain signals | Graphical insights ═══ * /}
      <div style={{ width: railTab === "insights" ? 540 : 336, flexShrink: 0, borderLeft: `1px solid ${t.c.border}`, background: t.c.surface, display: "flex", flexDirection: "column", minHeight: 0, transition: "width .2s ease" }}>
        <div style={{ display: "flex", gap: 4, padding: 8, borderBottom: `1px solid ${t.c.border}`, flexShrink: 0 }}>
          {[["brain", "Brain", <BrainIcon size={12} />, t.c.ai], ["insights", "Insights", <Sparkles size={12} />, t.c.primary]].map(([key, label, icon, c]) => {
            const on = railTab === key;
            return (
              <button key={key} type="button" role="tab" aria-selected={on} onClick={() => pickRail(key)}
                style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 10px", borderRadius: 8, border: `1px solid ${on ? t.c.border : "transparent"}`, background: on ? t.c.surfaceMuted : "transparent", color: on ? t.c.text : t.c.textSecondary, fontSize: 12.5, fontWeight: 650, cursor: on ? "default" : "pointer", fontFamily: t.font.family }}>
                <span style={{ color: on ? c : "inherit", display: "inline-flex" }}>{icon}</span>{label}
              </button>
            );
          })}
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          {railTab === "insights"
            ? <BrainInsights t={t} range={d.range} onOpenPerson={(p) => openMember({ id: p.id, name: p.name })} />
            : <RightRail t={t} d={d} onMove={runMoveAction} embedded />}
        </div>
      </div>

      */}
      <ModulePanel
        open={!!modulePanel}
        module={modulePanel}
        d={d}
        onOpenScreen={openModuleScreen}
        onClose={() => setModulePanel(null)}
      />

      {/* ═══ Drill-down ═══
          People AND projects go through the same drawer the Classic dashboard
          uses, so the two views cannot drift apart. The person AI chat that
          used to live in PersonPanel is now the drawer's Level-2 "Brain" tab;
          PersonPanel / PersonStage remain on disk, unwired. */}
      <DrilldownDrawer
        open={!!drillSubject}
        subject={drillSubject}
        range={d.range}
        people={d.people}
        onClose={closeDrill}
        /* project → contributor → that person's own drill-down, in place.
           Clearing panelPerson first, or the person subject would win over
           the one we are hopping to (drillSubject prefers panelPerson). */
        onNavigate={(next) => {
          if (next?.kind === "person") {
            setDrawer(null);
            setPanelPerson(personFor(next.id, next.name));
          } else {
            setPanelPerson(null);
            setDrawer(next);
          }
        }}
      />
    </div>
  );
}

/** Human label for the window being loaded — the skeleton says WHICH window. */
function periodLabel(period) {
  if (typeof period === "string" && period.startsWith("custom:")) {
    const [, a, b] = period.split(":");
    const f = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
    return `${f(a)} – ${f(b)}`;
  }
  return { today: "today", yesterday: "yesterday", "7d": "the last 7 days", "30d": "the last 30 days" }[period] || "this window";
}

/* ── Window control — antd Segmented + DatePicker.RangePicker ─────────────
   Presets and the calendar are one control: the Segmented carries the four
   common windows, "Custom" opens antd's own range calendar. Using antd here
   rather than two bare <input type="date"> means presets inside the calendar,
   keyboard nav, locale-correct formatting and a disabled-future rule all come
   from the design system instead of being re-implemented badly.

   The value is a single string — "today" | "yesterday" | "7d" | "30d" |
   "custom:<start>:<end>" — so `period` stays serialisable and every cache key
   and effect dependency downstream keeps working unchanged. */
function WindowControl({ t, period, range, onChange }) {
  const isCustom = typeof period === "string" && period.startsWith("custom:");

  const value = React.useMemo(() => {
    if (!isCustom || !range) return null;
    return [dayjs(range.start), dayjs(range.end)];
  }, [isCustom, range]);

  return (
    <>
      <Segmented
        size="small"
        // While a custom range is active NO preset is selected — showing
        // "Today" highlighted next to a 1–15 Aug range would be a lie about
        // which window is on screen.
        value={isCustom ? "" : period}
        onChange={onChange}
        options={[
          { label: "Today", value: "today" },
          { label: "Yesterday", value: "yesterday" },
          { label: "7 days", value: "7d" },
          { label: "30 days", value: "30d" },
        ]}
      />

      <Divider type="vertical" style={{ margin: "0 2px", height: 18, borderColor: t.c.border }} />

      {/* A REAL control, not a hidden trigger.
          The first version parked an invisible zero-width RangePicker at the end
          of the row and drove it from a "Custom" chip. Two things were broken by
          construction: antd anchors the calendar popup to that element, so it
          opened detached from the chip; and the chip could never stay selected,
          because its handler returned early without calling onChange and the
          controlled Segmented snapped back to the previous preset. */}
      <DatePicker.RangePicker
        size="small"
        /* Opts out of the global `.ant-picker ... padding-right: 60px !important`
           override in index.css, which reserves room for an absolutely
           positioned suffix and pushes both values out of a compact control.
           See the tg-dash-picker block at the bottom of index.css. */
        className="tg-dash-picker"
        value={value}
        allowClear={false}
        // "D MMM" keeps a picked range compact ("1 Aug – 15 Aug") while still
        // being unambiguous; the year is implied by the window being browsable
        // only up to today.
        format="D MMM"
        separator="–"
        // Both ends are labelled. The previous placeholders were longer than
        // the 178px the control was given, so they clipped to nothing and the
        // picker rendered as a bare separator and an icon.
        placeholder={["Start date", "End date"]}
        suffixIcon={<CalendarRange size={13} />}
        // A window that has not happened yet has no data behind it.
        disabledDate={(d) => d && d > dayjs().endOf("day")}
        presets={[
          { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] },
          { label: "Last 14 days", value: [dayjs().subtract(13, "day"), dayjs()] },
          { label: "Last 30 days", value: [dayjs().subtract(29, "day"), dayjs()] },
          { label: "This month", value: [dayjs().startOf("month"), dayjs()] },
          {
            label: "Last month",
            value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")],
          },
        ]}
        onChange={(vals) => {
          if (!vals || !vals[0] || !vals[1]) return;
          onChange(`custom:${vals[0].format("YYYY-MM-DD")}:${vals[1].format("YYYY-MM-DD")}`);
        }}
        // Wide enough for "Start date – End date" at the small font; a picked
        // range ("1 Aug – 15 Aug") is shorter and sits comfortably inside it.
        style={{ width: 208 }}
      />
    </>
  );
}

/* Small icon+label used inside antd Segmented options. */
function Chip({ icon, text }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>{icon}{text}</span>
  );
}
